import { lazy, Suspense } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";
import MapboxSiteMap from "./MapboxSiteMap";
import { FieldDetailPanel, ScopeChip } from "./FieldDetailPanel";

// Site Map — geographic Mapbox GL map (real as-built coordinates) when a
// Mapbox token is configured, otherwise the original schematic plan.
//   VITE_OFFLINE_MAP=true → the prototype offline-capable MapLibre map
//   (cacheable Esri basemap + map-gl-offline tile pre-download). Lazy-loaded so
//   the heavy maplibre-gl bundle only ships when the prototype is enabled.
export default function SiteMapView() {
  if (import.meta.env.VITE_OFFLINE_MAP === "true") return <OfflineSiteMapLazy />;
  if (import.meta.env.VITE_MAPBOX_TOKEN) return <MapboxSiteMap />;
  return <SchematicSiteMap />;
}

const OfflineSiteMapInner = lazy(() => import("./OfflineSiteMap"));
function OfflineSiteMapLazy() {
  return (
    <Suspense fallback={<div className="map-overlay" style={{ padding: 24, color: "var(--dim)" }}>Loading offline map…</div>}>
      <OfflineSiteMapInner />
    </Suspense>
  );
}

// Original schematic field plan: punch-pin overlay + popup, per-field table
// grid, accept/fail panel. Faithful port of vSiteMap().
function SchematicSiteMap() {
  const version = useGrid((s) => s.version);
  const bump = useGrid((s) => s.bump);
  const setView = useGrid((s) => s.setView);
  void version;

  const e = eng();
  const C = e.C;
  const D = e.D;

  const fieldRoll = (fl: any) => {
    let pass = 0;
    let fail = 0;
    for (let t = 1; t <= fl.tables; t++) {
      const s = e.tableEffectiveStatus(fl.id, t).status;
      if (s === "pass") pass++;
      else if (s === "fail") fail++;
    }
    return { pass, fail, done: pass + fail };
  };

  const showPins = e.state.mapShowPins !== false;
  const allPins = showPins ? e.punchPinsForMap({ showResolved: false }) : [];
  const pinsPerField: Record<string, number> = {};
  allPins.forEach((pin: any) => {
    const it = pin.item;
    if (pin.anchor !== "field") return;
    let fldId = it.fid;
    if (it.workScope === "station") {
      const st = D.stations.find((s: any) => s.id === it.fid);
      const m = st && (st.area || "").match(/Field\s+(\d+)/i);
      fldId = m ? "Field " + m[1] : null;
    }
    if (fldId) pinsPerField[fldId] = (pinsPerField[fldId] || 0) + 1;
  });

  const selectField = (id: string) => {
    e.state.selField = id;
    e.state.selTable = null;
    bump();
  };
  const togglePins = () => {
    e.state.mapShowPins = !e.state.mapShowPins;
    e.state.mapPin = null;
    bump();
  };
  const openPin = (id: string, ev: React.MouseEvent) => {
    ev.stopPropagation();
    e.state.mapPin = id;
    bump();
  };
  const closePin = () => {
    e.state.mapPin = null;
    bump();
  };
  const jumpToQC = (it: any) => {
    e.state.mapPin = null;
    e.state.qcItem = +it.idx;
    if (it.workScope === "station") {
      e.state.qcWorkScope = "station";
      e.state.qcStation = it.fid;
      e.state.qcInverter = it.t - 1;
    } else if (it.workScope === "site") {
      e.state.qcWorkScope = "site";
    } else {
      e.state.qcWorkScope = "table";
      e.state.qcField = it.fid;
      e.state.qcTable = +it.t;
      const fl = D.fields.find((x: any) => x.id === it.fid);
      if (fl) {
        for (let r = 1; r <= e.rowsForField(fl); r++) {
          if (e.tablesInRow(fl, r).includes(+it.t)) {
            e.state.qcRow = r;
            break;
          }
        }
      }
      e.state.qcModule = null;
      e.state.qcPile = null;
      e.state.showModules = false;
      e.state.showPiles = false;
      if (it.scopeKey && it.scopeKey[0] === "M") {
        e.state.qcModule = +it.scopeKey.slice(1);
        e.state.showModules = true;
      } else if (it.scopeKey && it.scopeKey[0] === "P") {
        e.state.qcPile = +it.scopeKey.slice(1);
        e.state.showPiles = true;
      }
    }
    setView("qc");
  };

  const fieldPins = showPins ? allPins.filter((p: any) => p.anchor === "field") : [];
  const sitePins = showPins ? allPins.filter((p: any) => p.anchor === "site") : [];
  const popupPin = e.state.mapPin && showPins ? allPins.find((p: any) => p.id === e.state.mapPin) : null;

  return (
    <div className="mapview">
      <div className="mapstage" id="mapstage" onClick={() => e.state.mapPin && closePin()}>
        {D.fields.map((fl: any) => {
          const r = fieldRoll(fl);
          const col = r.fail > 0 ? C.rust : r.pass > 0 ? C.grass : C.dim2;
          const pct = fl.tables ? Math.round((r.done / fl.tables) * 100) : 0;
          const pinCount = pinsPerField[fl.id] || 0;
          return (
            <div
              key={fl.id}
              className={`field ${fl.id === e.state.selField ? "sel" : ""}`}
              onClick={(ev) => {
                ev.stopPropagation();
                selectField(fl.id);
              }}
              style={{ left: `${fl.x}%`, top: `${fl.y}%`, width: `${fl.w}%`, height: `${fl.h}%` }}
            >
              <div className="fdot" style={{ background: col }} />
              <div className="fid">{fl.id}</div>
              <div className="fmeta">
                {fl.tables} tbl · {r.done}/{fl.tables} inspected
              </div>
              <div className="fpbar">
                <i style={{ width: `${pct}%`, background: col }} />
              </div>
              {showPins && pinCount > 0 ? (
                <div className="fpinbadge" title={`${pinCount} open punch item${pinCount === 1 ? "" : "s"} in this field`}>
                  {pinCount}
                </div>
              ) : null}
            </div>
          );
        })}

        {fieldPins.map((pin: any) => (
          <button
            key={pin.id}
            className="ppin"
            onClick={(ev) => openPin(pin.id, ev)}
            style={{ left: `${pin.x}%`, top: `${pin.y}%`, ["--pc" as any]: pin.color }}
            title={`${pin.item.item} — ${pin.item.loc}${pin.item.ncr ? ` · ${pin.item.ncr}` : ""}`}
          />
        ))}

        {sitePins.length ? (
          <div className="ppin-site-stack" title={`${sitePins.length} site-wide open item${sitePins.length === 1 ? "" : "s"}`}>
            <div className="ppin-site-lbl">Site-wide</div>
            {sitePins.map((pin: any) => (
              <button key={pin.id} className="ppin ppin-site" onClick={(ev) => openPin(pin.id, ev)} style={{ ["--pc" as any]: pin.color }} title={pin.item.item} />
            ))}
          </div>
        ) : null}

        {popupPin ? (
          <div
            className="ppin-popup"
            onClick={(ev) => ev.stopPropagation()}
            style={{
              left: popupPin.anchor === "site" ? "94%" : `${popupPin.x}%`,
              top: popupPin.anchor === "site" ? "88%" : `${popupPin.y}%`,
            }}
          >
            <button className="ppin-popup-x" onClick={closePin} aria-label="Close">
              ✕
            </button>
            <div className="ppin-popup-h">
              <span className="ppin-dot" style={{ background: popupPin.color }} />
              <ScopeChip scope={popupPin.item.scope} />
              {popupPin.item.ncr ? <span className="ppin-ncr">{popupPin.item.ncr}</span> : <span className="ppin-open">OPEN</span>}
            </div>
            <div className="ppin-popup-t">{popupPin.item.item}</div>
            <div className="ppin-popup-loc">{popupPin.item.loc}</div>
            <div className="ppin-popup-meta">
              <span className="mono">{popupPin.item.itp}</span>
              {popupPin.item.by ? ` · ${popupPin.item.by}` : ""}
              {popupPin.item.at ? ` · ${popupPin.item.at}` : ""}
            </div>
            {popupPin.item.comment ? <div className="ppin-popup-note">"{popupPin.item.comment}"</div> : null}
            <button className="obtn ok ppin-popup-go" onClick={() => jumpToQC(popupPin.item)}>
              Open in Quality Control →
            </button>
          </div>
        ) : null}

        <svg className="compass" viewBox="0 0 60 60">
          <circle cx="30" cy="30" r="26" fill="none" stroke={C.line} />
          <path d="M30 6 L34 30 L30 28 L26 30 Z" fill={C.hiviz} />
          <path d="M30 54 L34 30 L30 32 L26 30 Z" fill={C.dim2} />
          <text x="30" y="16" textAnchor="middle" fill={C.dim} fontSize="7" fontFamily="IBM Plex Mono">
            N
          </text>
        </svg>

        <div className="mlegend">
          <b>Table acceptance</b>
          <div>
            <i style={{ background: C.grass }} />
            Accepted
          </div>
          <div>
            <i style={{ background: C.rust }} />
            Failed
          </div>
          <div>
            <i style={{ background: C.dim2 }} />
            Not inspected
          </div>
          {showPins ? (
            <>
              <div className="mlegend-sep" />
              <b>Punch pins</b>
              <div>
                <i className="legpin" style={{ background: C.rust }} />
                Open · NCR raised
              </div>
              <div>
                <i className="legpin" style={{ background: C.amber }} />
                Open · no NCR
              </div>
            </>
          ) : null}
        </div>

        <button
          className={`mappins-toggle ${showPins ? "on" : ""}`}
          onClick={(ev) => {
            ev.stopPropagation();
            togglePins();
          }}
          title={`${showPins ? "Hide" : "Show"} punch-list pins on the map`}
        >
          <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6c0 3.4 4.5 8.5 4.5 8.5s4.5-5.1 4.5-8.5C12.5 3.5 10.5 1.5 8 1.5z" />
            <circle cx="8" cy="6" r="1.5" />
          </svg>
          {showPins ? "Pins on" : "Pins off"}
          {showPins && allPins.length ? <span className="mappins-count">{allPins.length}</span> : null}
        </button>

        <div className="scalebar">
          <span>schematic layout · not to scale</span>
          <div className="bar" />
          <span>13 fields · Ballyroe</span>
        </div>
        <div className="maphint">Click a field, then click any table to mark it accepted or failed.</div>
      </div>

      <FieldDetailPanel />
    </div>
  );
}
